import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Spinner } from './Spinner'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
type Size = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const variants: Record<Variant, string> = {
  primary:
    'bg-ink text-ink-fg hover:bg-ink-hover shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(0,0,0,0.12)]',
  secondary:
    'bg-field text-fg border border-field-border hover:bg-hover shadow-[inset_0_1px_0_var(--glass-highlight)]',
  ghost: 'text-fg-2 hover:bg-hover hover:text-fg',
  subtle: 'bg-hover text-fg hover:bg-press',
  danger: 'bg-danger-soft text-danger hover:bg-danger hover:text-white border border-transparent',
}

const sizes: Record<Size, string> = {
  sm: 'h-[calc(var(--spacing)*7*var(--ui-size))] px-[calc(var(--spacing)*2.5*var(--ui-size))] text-[length:calc(13px*var(--text-scale)*var(--ui-size))] gap-1.5 rounded-[calc(8px*var(--radius-scale))]',
  md: 'h-[calc(var(--spacing)*8.5*var(--ui-size))] px-[calc(var(--spacing)*3.5*var(--ui-size))] text-[length:calc(13.5px*var(--text-scale)*var(--ui-size))] gap-2 rounded-[var(--radius-control)]',
  lg: 'h-[calc(var(--spacing)*10*var(--ui-size))] px-[calc(var(--spacing)*4.5*var(--ui-size))] text-[length:calc(14px*var(--text-scale)*var(--ui-size))] gap-2 rounded-[var(--radius-control)]',
  icon: 'size-[calc(var(--spacing)*8.5*var(--ui-size))] rounded-[var(--radius-control)]',
  'icon-sm': 'size-[calc(var(--spacing)*7*var(--ui-size))] rounded-[calc(8px*var(--radius-scale))]',
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  trailing?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, trailing, className, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap font-medium tracking-[-0.005em]',
        'transition-[background-color,color,box-shadow,transform,opacity] duration-[var(--dur-fast)] ease-out active:scale-[0.97]',
        'disabled:pointer-events-none disabled:opacity-45',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner className="size-3.5" /> : icon}
      {children}
      {trailing}
    </button>
  )
})
